import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import FhirDateTime from '@lifeomic/phc-web-toolkit/src/util/fhirResourceParsers/FhirDateTime';
import moment from 'moment';
import { memberDetailMessages } from './messages';
import { useDataset } from '../../../../util/api';
import { usePractitionerNames } from '../../../../util/hooks/usePractitionerNames';
import { ExtractedResourceProvenance } from './ExtractedResourceProvenance';

const medicationRequestFhirService = new FhirService('MedicationRequest');
const medicationStatementFhirService = new FhirService('MedicationStatement');
const dateTimeParser = new FhirDateTime();

type MedicationResource = fhir.MedicationRequest | fhir.MedicationStatement;

const isMedicationRequest = (
  resource: MedicationResource,
): resource is fhir.MedicationRequest =>
  resource.resourceType === 'MedicationRequest';

const isMedicationStatement = (
  resource: MedicationResource,
): resource is fhir.MedicationStatement =>
  resource.resourceType === 'MedicationStatement';

const useStyles = makeStyles(theme => ({
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.common.white,
    position: 'relative',
  },
  dosageCell: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  medicationCell: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  modalSection: {
    marginBottom: 24,
  },
  modalSectionTitle: {
    marginBottom: 12,
    fontSize: '1rem',
    fontWeight: 600,
  },
  modalField: {
    marginBottom: 12,
  },
  modalLabel: {
    display: 'block',
    marginBottom: 4,
    fontWeight: 600,
  },
  modalValue: {
    display: 'block',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: 1.5,
  },
}));

/** Extract practitioner ID from MedicationRequest.requester (R4 agent.reference or DSTU3 reference). */
const getRequesterPractitionerId = (
  resource: MedicationResource,
): string | null => {
  if (!isMedicationRequest(resource)) return null;
  const requester = resource.requester;
  if (!requester || typeof requester !== 'object') return null;
  // R4: requester.agent.reference
  const ref =
    (requester as { agent?: { reference?: string } }).agent?.reference ??
    (requester as { reference?: string }).reference;
  if (!ref || !ref.startsWith('Practitioner/')) return null;
  return ref.slice('Practitioner/'.length) || null;
};

/** Display from resource when embedded (e.g. agent.display), otherwise use resolved names. */
const getRequesterDisplay = (
  resource: MedicationResource,
  resolvedNames: Record<string, string>,
): string => {
  if (isMedicationStatement(resource)) {
    return (
      resource.informationSource?.display ||
      resource.informationSource?.reference ||
      '—'
    );
  }
  const requester = resource.requester;
  if (!requester) return '—';
  // Check if requester has agent.display (FHIR R4+ structure)
  if (
    typeof requester === 'object' &&
    'agent' in requester &&
    requester.agent &&
    typeof requester.agent === 'object' &&
    'display' in requester.agent &&
    typeof requester.agent.display === 'string'
  ) {
    return requester.agent.display;
  }
  // Fallback to requester.display if available (FHIR DSTU3 structure)
  if (
    typeof requester === 'object' &&
    'display' in requester &&
    typeof requester.display === 'string'
  ) {
    return requester.display;
  }
  const id = getRequesterPractitionerId(resource);
  if (id && resolvedNames[id]) return resolvedNames[id];
  return '—';
};

export const getMedicationDosage = (resource: MedicationResource) =>
  isMedicationRequest(resource)
    ? resource.dosageInstruction?.[0]
    : resource.dosage?.[0];

export const getMedicationDisplay = (resource: MedicationResource): string => {
  return (
    resource.medicationCodeableConcept?.coding?.[0]?.display ||
    resource.medicationCodeableConcept?.text ||
    resource.medicationReference?.display ||
    '—'
  );
};

const getMedicationStatementEffectiveDisplay = (
  resource: fhir.MedicationStatement,
): string => {
  if (resource.effectiveDateTime) {
    return dateTimeParser.getDisplayValue(resource.effectiveDateTime) || '—';
  }
  if (resource.effectivePeriod?.start || resource.effectivePeriod?.end) {
    const start = resource.effectivePeriod.start
      ? dateTimeParser.getDisplayValue(resource.effectivePeriod.start)
      : null;
    const end = resource.effectivePeriod.end
      ? dateTimeParser.getDisplayValue(resource.effectivePeriod.end)
      : null;
    return [start, end].filter(Boolean).join(' - ') || '—';
  }
  return '—';
};

export const getMedicationSortTime = (resource: MedicationResource): number => {
  if (isMedicationRequest(resource)) {
    return resource.authoredOn ? new Date(resource.authoredOn).getTime() : 0;
  }
  const effectiveDate =
    resource.effectiveDateTime || resource.effectivePeriod?.start;
  return effectiveDate ? new Date(effectiveDate).getTime() : 0;
};

export const getMedicationDateDisplay = (
  resource: MedicationResource,
): string => {
  if (isMedicationRequest(resource)) {
    return resource.authoredOn
      ? moment(resource.authoredOn).format('M/D/YYYY')
      : '—';
  }
  return isMedicationStatement(resource)
    ? getMedicationStatementEffectiveDisplay(resource)
    : '—';
};

type MedicationsRow = {
  id: string;
  resource: MedicationResource;
  medication: string;
  status: string;
  dosage: string;
  authoredOn: string;
  intent: string;
  prescriber: string;
};

type MedicationViewModalProps = {
  resource: MedicationResource;
  practitionerNames: Record<string, string>;
  subjectId: string;
  onDismiss: () => void;
  onRejected: (resourceId: string) => void;
};

const MedicationViewModal: React.FC<MedicationViewModalProps> = ({
  resource,
  practitionerNames,
  subjectId,
  onDismiss,
  onRejected,
}) => {
  const classes = useStyles({});
  const medicationName = getMedicationDisplay(resource);
  const status = resource.status || '—';
  const intent = isMedicationRequest(resource) ? resource.intent || '—' : '—';
  const authoredOn = isMedicationRequest(resource)
    ? resource.authoredOn
      ? dateTimeParser.getDisplayValue(resource.authoredOn) || '—'
      : '—'
    : isMedicationStatement(resource)
    ? getMedicationStatementEffectiveDisplay(resource)
    : '—';
  const prescriber = getRequesterDisplay(resource, practitionerNames);
  const reasonCode =
    resource.reasonCode?.[0]?.coding?.[0]?.display ||
    resource.reasonCode?.[0]?.text ||
    null;
  const note = resource.note?.[0]?.text || null;

  // Dosage instruction details
  const dosageInstruction = getMedicationDosage(resource);
  const dosageText = dosageInstruction?.text || null;
  const patientInstruction = dosageInstruction?.patientInstruction || null;
  const doseQuantity = dosageInstruction?.doseQuantity;
  const doseQuantityDisplay = doseQuantity
    ? `${doseQuantity.value || ''} ${doseQuantity.unit || ''}`.trim()
    : null;
  const timing = dosageInstruction?.timing?.code?.text || null;
  const route = dosageInstruction?.route?.text || null;
  const method = dosageInstruction?.method?.text || null;
  const asNeeded = dosageInstruction?.asNeededBoolean ?? null;

  // Dispense request details
  const dispenseRequest = isMedicationRequest(resource)
    ? resource.dispenseRequest
    : undefined;
  const validityPeriodStart = dispenseRequest?.validityPeriod?.start
    ? dateTimeParser.getDisplayValue(dispenseRequest.validityPeriod.start) ||
      null
    : null;
  const validityPeriodEnd = dispenseRequest?.validityPeriod?.end
    ? dateTimeParser.getDisplayValue(dispenseRequest.validityPeriod.end) || null
    : null;
  const quantity = dispenseRequest?.quantity;
  const quantityDisplay = quantity
    ? `${quantity.value || ''} ${quantity.unit || ''}`.trim()
    : null;
  const expectedSupplyDuration = dispenseRequest?.expectedSupplyDuration;
  const supplyDurationDisplay = expectedSupplyDuration
    ? `${expectedSupplyDuration.value || ''} ${
        expectedSupplyDuration.unit || ''
      }`.trim()
    : null;

  return (
    <Modal
      isOpen={true}
      size={1}
      fullWidth
      onDismiss={onDismiss}
      title={medicationName}
      data-testid="medication-view-modal"
    >
      <Box padding={2} direction="column">
        {/* Basic Information */}
        <Box direction="column" className={classes.modalSection}>
          <Text
            size="subbody"
            weight="bold"
            className={classes.modalSectionTitle}
          >
            Basic Information
          </Text>
          <Box direction="column" gap={2}>
            <Box direction="column" className={classes.modalField}>
              <Text size="subbody" weight="bold" className={classes.modalLabel}>
                Status
              </Text>
              <Text size="subbody" className={classes.modalValue}>
                {status}
              </Text>
            </Box>
            <Box direction="column" className={classes.modalField}>
              <Text size="subbody" weight="bold" className={classes.modalLabel}>
                Intent
              </Text>
              <Text size="subbody" className={classes.modalValue}>
                {intent}
              </Text>
            </Box>
            <Box direction="column" className={classes.modalField}>
              <Text size="subbody" weight="bold" className={classes.modalLabel}>
                Authored On
              </Text>
              <Text size="subbody" className={classes.modalValue}>
                {authoredOn}
              </Text>
            </Box>
            {prescriber !== '—' && (
              <Box direction="column" className={classes.modalField}>
                <Text
                  size="subbody"
                  weight="bold"
                  className={classes.modalLabel}
                >
                  Prescriber
                </Text>
                <Text size="subbody" className={classes.modalValue}>
                  {prescriber}
                </Text>
              </Box>
            )}
            {reasonCode && (
              <Box direction="column" className={classes.modalField}>
                <Text
                  size="subbody"
                  weight="bold"
                  className={classes.modalLabel}
                >
                  Reason
                </Text>
                <Text size="subbody" className={classes.modalValue}>
                  {reasonCode}
                </Text>
              </Box>
            )}
            {note && (
              <Box direction="column" className={classes.modalField}>
                <Text
                  size="subbody"
                  weight="bold"
                  className={classes.modalLabel}
                >
                  Note
                </Text>
                <Text size="subbody" className={classes.modalValue}>
                  {note}
                </Text>
              </Box>
            )}
          </Box>
        </Box>

        {/* Dosage Instructions */}
        {(dosageText ||
          patientInstruction ||
          doseQuantityDisplay ||
          timing ||
          route ||
          method ||
          asNeeded !== null) && (
          <Box direction="column" className={classes.modalSection}>
            <Text
              size="subbody"
              weight="bold"
              className={classes.modalSectionTitle}
            >
              Dosage Instructions
            </Text>
            <Box direction="column" gap={2}>
              {dosageText && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    Instructions
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {dosageText}
                  </Text>
                </Box>
              )}
              {patientInstruction && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    Patient Instructions
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {patientInstruction}
                  </Text>
                </Box>
              )}
              {doseQuantityDisplay && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    Dose
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {doseQuantityDisplay}
                  </Text>
                </Box>
              )}
              {timing && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    Timing
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {timing}
                  </Text>
                </Box>
              )}
              {route && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    Route
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {route}
                  </Text>
                </Box>
              )}
              {method && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    Method
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {method}
                  </Text>
                </Box>
              )}
              {asNeeded !== null && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    As Needed
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {asNeeded ? 'Yes' : 'No'}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Dispense Request */}
        {(validityPeriodStart ||
          validityPeriodEnd ||
          quantityDisplay ||
          supplyDurationDisplay) && (
          <Box direction="column" className={classes.modalSection}>
            <Text
              size="subbody"
              weight="bold"
              className={classes.modalSectionTitle}
            >
              Dispense Information
            </Text>
            <Box direction="column" gap={2}>
              {(validityPeriodStart || validityPeriodEnd) && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    Validity Period
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {validityPeriodStart && validityPeriodEnd
                      ? `${validityPeriodStart} - ${validityPeriodEnd}`
                      : validityPeriodStart || validityPeriodEnd || '—'}
                  </Text>
                </Box>
              )}
              {quantityDisplay && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    Quantity
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {quantityDisplay}
                  </Text>
                </Box>
              )}
              {supplyDurationDisplay && (
                <Box direction="column" className={classes.modalField}>
                  <Text
                    size="subbody"
                    weight="bold"
                    className={classes.modalLabel}
                  >
                    Expected Supply Duration
                  </Text>
                  <Text size="subbody" className={classes.modalValue}>
                    {supplyDurationDisplay}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        )}
        <ExtractedResourceProvenance
          resource={resource}
          resourceType={resource.resourceType ?? 'MedicationRequest'}
          subjectId={subjectId}
          onRejected={onRejected}
        />
      </Box>
    </Modal>
  );
};

export type MedicationsContentProps = {
  memberId: string;
  fallback: React.ReactNode;
};

export const MedicationsContent: React.FC<MedicationsContentProps> = ({
  memberId,
  fallback,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const accountId = useActiveAccountId();
  const auth = useServiceAuth();
  const dataset = useDataset();
  const [fetchMedicationRequests, fetchState] = useService(
    medicationRequestFhirService.get,
  );
  const [fetchMedicationStatements, fetchMedicationStatementsState] =
    useService(medicationStatementFhirService.get);
  const [medicationResources, setMedicationResources] = useState<
    MedicationResource[]
  >([]);
  const [selectedMedication, setSelectedMedication] =
    useState<MedicationResource | null>(null);

  const loadMedicationRequests = useCallback(async () => {
    if (!accountId || !memberId || !auth) return;
    try {
      const [requestResponse, statementResponse] = await Promise.all([
        fetchMedicationRequests(accountId, {
          subject: `Patient/${memberId}`,
          _tag: [dataset.codingToken],
          _count: 200,
        }),
        fetchMedicationStatements(accountId, {
          subject: `Patient/${memberId}`,
          _tag: [dataset.codingToken],
          _count: 200,
        }),
      ]);
      const requestEntry = requestResponse?.data?.entry ?? [];
      const medicationRequests = requestEntry
        .map((e: { resource?: fhir.MedicationRequest }) => e.resource)
        .filter(Boolean) as fhir.MedicationRequest[];
      const statementEntry = statementResponse?.data?.entry ?? [];
      const medicationStatements = statementEntry
        .map((e: { resource?: fhir.MedicationStatement }) => e.resource)
        .filter(Boolean) as fhir.MedicationStatement[];
      const sortedResources = [
        ...medicationRequests,
        ...medicationStatements,
      ].sort((a, b) => {
        const dateA = getMedicationSortTime(a);
        const dateB = getMedicationSortTime(b);
        return dateB - dateA;
      });
      setMedicationResources(sortedResources);
    } catch {
      setMedicationResources([]);
    }
  }, [
    accountId,
    memberId,
    auth,
    fetchMedicationRequests,
    fetchMedicationStatements,
    dataset.codingToken,
  ]);

  useEffect(() => {
    void loadMedicationRequests();
  }, [loadMedicationRequests]);

  const requesterPractitionerIds = React.useMemo(() => {
    const ids = new Set<string>();
    medicationResources.forEach(resource => {
      const id = getRequesterPractitionerId(resource);
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }, [medicationResources]);

  const practitionerNames = usePractitionerNames(requesterPractitionerIds);

  const rows: MedicationsRow[] = medicationResources.map(resource => ({
    id: resource.id ?? '',
    resource,
    medication: getMedicationDisplay(resource),
    status: resource.status || '—',
    dosage: getMedicationDosage(resource)?.text || '—',
    authoredOn: getMedicationDateDisplay(resource),
    intent: isMedicationRequest(resource) ? resource.intent || '—' : '—',
    prescriber: getRequesterDisplay(resource, practitionerNames),
  }));

  const columns = [
    {
      header: {
        label: formatMessage(memberDetailMessages.medicationsMedication),
      },
      cell: {
        content: (row: MedicationsRow) => row.medication,
        className: classes.medicationCell,
      },
    },
    {
      header: { label: formatMessage(memberDetailMessages.medicationsStatus) },
      cell: { content: (row: MedicationsRow) => row.status },
    },
    {
      header: { label: formatMessage(memberDetailMessages.medicationsIntent) },
      cell: { content: (row: MedicationsRow) => row.intent },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.medicationsPrescriber),
      },
      cell: { content: (row: MedicationsRow) => row.prescriber },
    },
    {
      header: { label: formatMessage(memberDetailMessages.medicationsDosage) },
      cell: {
        content: (row: MedicationsRow) => row.dosage,
        className: classes.dosageCell,
      },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.medicationsAuthoredOn),
      },
      cell: { content: (row: MedicationsRow) => row.authoredOn },
    },
  ];

  const handleRowClick = (row: MedicationsRow) => {
    setSelectedMedication(row.resource);
  };

  if (
    (fetchState.isFetching || fetchMedicationStatementsState.isFetching) &&
    medicationResources.length === 0
  ) {
    return <>{fallback}</>;
  }

  return (
    <div className={classes.tabPanel}>
      <TableModule
        isLoading={
          fetchState.isFetching || fetchMedicationStatementsState.isFetching
        }
        config={columns}
        data={rows}
        noResultsMessage={formatMessage(memberDetailMessages.medicationsEmpty)}
        onRowClick={handleRowClick}
        rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
        rowRole="link"
      />
      {selectedMedication && (
        <MedicationViewModal
          resource={selectedMedication}
          practitionerNames={practitionerNames}
          subjectId={memberId}
          onDismiss={() => setSelectedMedication(null)}
          onRejected={resourceId => {
            setMedicationResources(current =>
              current.filter(resource => resource.id !== resourceId),
            );
            setSelectedMedication(null);
          }}
        />
      )}
    </div>
  );
};
